import React, { useEffect, useState, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { X, Save, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { adminPolicyGet, adminPolicyCreate, adminPolicyUpdate } from "../api";

const field = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20";
const labelCls = "block text-xs font-semibold text-[#0D1B2A] mb-1 uppercase tracking-wide";

const empty = { title: "", slug: "", updated_label: "Last updated: August 2025", body: "", published: true, order: 0 };

const PolicyEditor = ({ policyId, onClose, onSaved }) => {
  const { toast } = useToast();
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(!!policyId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (policyId) {
      adminPolicyGet(policyId).then((p) => { setData({ ...empty, ...p }); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [policyId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "blockquote"],
      ["clean"],
    ],
  }), []);

  const save = async () => {
    if (!data.title) { toast({ title: "Title is required" }); return; }
    setSaving(true);
    try {
      if (policyId) await adminPolicyUpdate(policyId, data);
      else await adminPolicyCreate(data);
      toast({ title: "Policy saved 🎉 — changes are now live" });
      onSaved();
    } catch {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-[#0D1B2A]">{policyId ? "Edit Policy" : "New Policy"}</h2>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & Publish
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-[#475569]">Loading…</div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Title</label><input className={field} value={data.title} onChange={(e) => set("title", e.target.value)} /></div>
              <div><label className={labelCls}>Slug (optional)</label><input className={field} value={data.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-from-title" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>Updated label</label><input className={field} value={data.updated_label} onChange={(e) => set("updated_label", e.target.value)} /></div>
              <div><label className={labelCls}>Order</label><input className={field} type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></div>
            </div>
            <div>
              <label className={labelCls}>Policy Content</label>
              <div className="pp-quill">
                <ReactQuill theme="snow" value={data.body} onChange={(v) => set("body", v)} modules={modules} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0D1B2A]">
              <input type="checkbox" className="w-4 h-4 accent-[#06B6D4]" checked={data.published} onChange={(e) => set("published", e.target.checked)} />
              Published (visible on the website)
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyEditor;
